import React, { ChangeEvent, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import { checkChange } from "utils/checkChange";
import { imageToBase64 } from "utils/imageToBase64";
import { useAlert } from "hooks/useAlert";

import facebook from 'assets/social-media-icons/facebook.svg';
import twitter from 'assets/social-media-icons/twitter.svg';
import youtube from 'assets/social-media-icons/youtube.svg';
import instagram from 'assets/social-media-icons/instagram.svg';
import tiktok from 'assets/social-media-icons/tiktok.svg';
import { useToast } from "hooks/useToast";
import { CheckListProps, ContactProps, ContactTypeProps, LeadProps, SellersLeadPropertiesResponse, SocialMediaDictionary, StatusAnalysisLeadProps, StatusUsernameLeadProps } from "../SellerLeadForm";
import { useHistory } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { formattingNumberToText } from "utils/formattingInputs";
import { noDdiPhone } from "utils/regex";

interface ActionAlertProps {
  inputType?: string,
  inputName?: string,
}

const initInputsValues:LeadProps = {
  firstname: '',
  email: '',
  cnpj: '',
  company: '',
  website: '',
  logo: '',
  phone: '',
  segmentId: 0,
  ecommercePlatformId: 0,
  billingRangeId: 0,
  amountRangeId: 0,
  userId: 0,
  contacts: [{
    contactTypeId: 0,
    title: '',
    contact: '',
  }],
  socialMedias: [
    {
      contactTypeId: 0,
      contact: '',
    }
  ],
}

const initProperties = {
  billingRange: [{
    id: '',
    description: ''
  }],
  amountRange: [{
    id: '',
    description: '',
    sellerClassification: '',
  }],
  ecommercePlatform: [{
    id: '',
    description: ''
  }],
  segment: [{
    id: '',
    description: ''
  }],
  contactSocialMediaType: [{
    contactTypeId: 0, 
    contactType: ''
  }],
  contactType: [{
    contactTypeId: 0, 
    contactType: ''
  }],
}

const initTouchedInputsValues = {
  leadId: false,
  clearsaleEntityId: false,
  firstname: false,
  email: false,
  cnpj: false,
  company: false,
  website: false,
  segmentId: false,
  ecommercePlatformId: false,
  billingRangeId: false,
  amountRangeId: false, 
  logo: false,
  phone: false,
  ramal: false,
  socialMediaLink: false,
  leadStatusId: false,
  score: false,
  riskSuggestion: false,
  cnpjStatus: false,
  userId: false,
  contacts: [{
    contactTypeId: false,
    title: false,
    contact: false,
  }],
}

const initUsers = [{
  userId: 0,
  userName: ''
}]

const numberRangeToText = (rangeStart:number, rangeEnd?:number, currency?:boolean) => {

  const result = rangeEnd ? `De ${currency ? 'R\$' : ''}${formattingNumberToText(rangeStart)} a ${currency ? 'R\$' : ''}${formattingNumberToText(rangeEnd)}`
    : `Acima de ${currency ? 'R\$' : ''} ${formattingNumberToText(rangeStart)}`

  return result;
}


export const useSellerLeadNew = () => {
  const {setToast} = useToast();
  const {push} = useHistory();
  const { alert, setTextAlertInterface, openAlert, closeAlert } = useAlert();
  const { connectApi, connectAllApi } = useAuth();

  const [statusAnalysisLead, setStatusAnalysisLead] = useState<StatusAnalysisLeadProps[]>([]); 
  const [properties, setProperties] = useState<SellersLeadPropertiesResponse>(initProperties);
  const [contactTypes, setContactTypes] = useState<ContactTypeProps[]>([]);
  const [socialMediaTypes, setSocialMediaTypes] = useState<ContactTypeProps[]>();
  const [usersOptions, setUsersOptions] = useState<StatusUsernameLeadProps[]>(initUsers); 
  const [actionAlert, setActionAlert] = useState<ActionAlertProps>();
  const [checklist, setChecklist] = useState<CheckListProps[]>([]); 
  const [imagePath, setImagePath] = useState<string>('');
  const [initialInputsValues, setInitialInputsValues] = useState(initInputsValues);
  const [changeFooter, setChangeFooter] = useState<string[]>([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [logoIsPNG, setLogoIsPNG] = useState(false);
  const [category, setCategory] = useState('');
  const {
    handleSubmit, 
    handleChange,
    resetForm, 
    setValues: setInputsValues, 
    values: inputsValues, 
    touched, 
    errors
  } = useFormik({
    initialValues: initInputsValues,
    initialTouched: initTouchedInputsValues,
    validationSchema: Yup.object({
      firstname: Yup.string().optional(),
      email: Yup.string().email().optional(),
      cnpj: Yup.string().required().matches(/^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}\-?\d{2}$/),
      company: Yup.string().required(),
      website: Yup.string().required(),
      logo: Yup.string().optional(),
      phone: Yup.string().matches(noDdiPhone).optional(),
      segmentId: Yup.number().nullable(true).optional(),
      ecommercePlatformId: Yup.number().nullable(true).optional(),
      billingRangeId: Yup.number().nullable(true).optional(),
      amountRangeId: Yup.number().nullable(true).optional(),
      userId: Yup.number().not([0]).required(),
      contacts: Yup.array().of(
        Yup.object({
          contactTypeId: Yup.number().optional(),
          contact: Yup.string().optional(),
          title: Yup.string().optional(),
        })
      ).min(1),
      socialMedias: Yup.array().of(
        Yup.object({
          contactTypeId: Yup.number().not([0]),
          contact: Yup.string(),
        })
      ),
    }),
    onSubmit: (_) => {
      sendData();
    }
  });

  const changeNameTag = (props: string) => (
    {
      "firstname": "Nome do responsável",
      "company": "Nome da loja",
      "email": "Email",
      "phone": "Telefone",
      "cnpj": "CNPJ",
      "website": "URL da loja",
      "segmentId": "Categoria",
      "ecommercePlatformId": "Plataforma",
      "amountRangeId": "Quantidade de vendas / mês",
      "billingRangeId": "Faturamento mensal",
      "notificationApp": "Notificar no app",
      "contacts": "Contatos de suporte",
      "logo": "Logo",
      "leadStatus": "Status",
      "username": "Responsável",
    }
  )[props];

  const socialMedias:SocialMediaDictionary = {
    'facebook': facebook,
    'twitter': twitter,
    'youtube': youtube,
    'instagram': instagram,
    'tiktok': tiktok,
  }

  //AÇÕES DO USUÁRIO
  const handleRemoveContact = (contactIndex: number) => {
    const newContacts = [...inputsValues.contacts];
    newContacts.splice(contactIndex, 1);

    setInputsValues({
      ...inputsValues,
      contacts: newContacts,
    })
  }

  const handleAddContact = () => {
    const newContact:ContactProps = {
      contactTypeId: 0,
      title: '',
      contact: '',
    }
    setInputsValues({
      ...inputsValues,
      contacts: [
        ...inputsValues.contacts,
        newContact,
      ]
    })
  }

  const handlerImageSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (event.target?.files) {
      const imageUrl = window.URL.createObjectURL(event.target.files[0]);
      const imageFile = event.target.files[0];
      if(imageFile.type === "image/png"){
        setLogoIsPNG(true)
        if(imageFile)
        imageToBase64(imageFile).then((result) => {
          setInputsValues({
            ...inputsValues,
            logo: result,
          })
        })
        setImagePath(imageUrl);
      }else{
        setLogoIsPNG(false)
        setImagePath('dont PNG');
      }
    }
  }

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event)
  }

  const discardChanges = () => {
    resetForm();
    setInputsValues({
      ...initialInputsValues
    });
    
    setImagePath("");
    closeAlert();
  }

  const saveFormChanges = () => {
    handleSubmit();
    if (Object.keys(errors).length > 0) {
      setToast({
        message: `Favor corrigir o(s) campo(s) indicado(s)`,
        isActive: true,
        type: 'error',
        timeToRemoveToast: 3000
      })
      closeAlert();
      return
    }
  }

  const showAlertForm = (typeOpen: boolean) => {
    setActionAlert({inputType: "formChanges"});
    setTextAlertInterface({
      positionCenter: false,
    })
    openAlert(typeOpen, 'sellerLead');
  }

  //FUNÇÕES DE PERSISTENCIA
  const getData = async () => {
    setIsLoading(true);
    connectAllApi([
      {type: 'get', url: 'sellers/properties'},
      {type: 'get', url: "leads/steps"},
      {type: 'get', url: "leads/status"},
      {type: 'get', url: "users?pageNumber=1&rowsPerPage=10000"},
    ]).then((responses) => {
      if (responses[0].status !== "rejected") {
        setHasError(false);
        const localProperties: SellersLeadPropertiesResponse = responses[0].value.data;
        setProperties({...localProperties});

        const segment = localProperties.segment.map((item: any) => ({
          id: item.segmentId, 
          description: item.segment
        }));

        const ecommercePlatform = localProperties.ecommercePlatform.map((item: any) => ({
          id: item.ecommercePlatformId,
          description: item.ecommercePlatform
        }));

        const amountRange = localProperties.amountRange.map((item: any, index:any) => ({
          id: item.amountRangeId,
          description: index < localProperties.amountRange.length-1 
            ? numberRangeToText(item.amountRangeStart, item.amountRangeEnd) 
            : numberRangeToText(item.amountRangeStart),
          sellerClassification: item.sellerClassification,
        }));

        const billingRange = localProperties.billingRange.map((item: any, index:any) => ({
          id: item.billingRangeId, 
          description: index < localProperties.billingRange.length-1 ? 
            numberRangeToText(item.billingRangeStart, item.billingRangeEnd) 
            : numberRangeToText(item.billingRangeStart, 0, true)
        }))

        setProperties({
          ...localProperties,
          segment,
          ecommercePlatform,
          amountRange,
          billingRange
        });

        const socialMediaTypesDecoded:ContactTypeProps[] = responses[0].value.data?.contactSocialMediaType;
        const contactTypesDecoded:ContactTypeProps[] = responses[0].value.data?.contactType;

        const newInitInputsValues:LeadProps = {
          ...initInputsValues,
          socialMedias: socialMediaTypesDecoded.map(item => ({
            leadContactId: item?.leadContactId,
            contactTypeId: item.contactTypeId,
            contact: '',
          }))
        }

        setInitialInputsValues({...newInitInputsValues});
        setInputsValues({...newInitInputsValues});
        setContactTypes(contactTypesDecoded);
        setSocialMediaTypes(socialMediaTypesDecoded);
      } else {
        setHasError(true);
      }

      if(responses[1].status !== "rejected"){
        setChecklist(responses[1].value.data)
      }else{
        setHasError(true);
      }
      
      if(responses[2].status !== "rejected"){
        setStatusAnalysisLead(responses[2].value.data)
      }else{
        setHasError(true);
      }
      
      if(responses[3].status !== "rejected"){
        setUsersOptions(responses[3].value.data)
      }else{
        setHasError(true);
      }
    })
    .catch((_) => setHasError(true))
    .finally(() => setIsLoading(false));
  }

  const sendData = async () => {
    const dataToSave = {
      ...inputsValues,
      socialMedias: inputsValues.socialMedias?.filter(item => item.contact !== ""),
      phone: inputsValues.phone
    }
    
    connectApi({
      type: 'post',
      url: '/leads',
      body: dataToSave,
      successMessage: "Lead salvo com sucesso"
    }).then((response) => {
      const id = response.data || "";
      setToast({
        isActive: true,
        message:"Lead salvo com sucesso",
        timeToRemoveToast: 3000,
        type: "success"
      })
      setActionAlert({});
      id ? push(`/sellers/lead/${id}/edit`, inputsValues.company) : push(`/sellers/leads`)
    }).finally(() => closeAlert());
  }

  useEffect(() => {
    alert.typeContinue === "save" && saveFormChanges();
    alert.typeContinue === "discard" && discardChanges();
  }, [changeFooter, alert])
  
  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setChangeFooter(changed);
  }, [inputsValues])
  
  useEffect(() => {    
    if(Number(inputsValues.amountRangeId) > 0){
      setCategory((properties.amountRange.filter(item => Number(item.id) === Number(inputsValues.amountRangeId))[0].sellerClassification || ''))
    }
  }, [inputsValues.amountRangeId])

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues])

  useEffect(() => {
    getData();
  }, [])


  return {
    handleInputChange,
    checklist,
    inputsValues,
    touched,
    errors,
    handleSelectChange,
    properties,
    imagePath,
    handlerImageSelected,
    socialMedias,
    handleRemoveContact,
    handleAddContact,
    changeFooter,
    handleSubmit,
    showAlertForm,
    submitButton,
    changeNameTag,
    hasError,
    isLoading,
    statusAnalysisLead,
    usersOptions,
    category,
    checkListStatus: false,
    businnesTrust: false,
    socialMediaTypes,
    contactTypes,
    logoIsPNG
  }
}